import { useQuery } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import http from '@/services/http';
import { entitySlugFor, isSearchableEntity } from '@/composables/entityColumns';
import { liveRecords } from '@/composables/liveRecords/liveRecords';

/**
 * Fetch one record by entity + id for the side-peek inspector surfaces.
 *
 * Searchable entities go through their search endpoint (id filter) so the row
 * carries the full search document — flattened fields, nested display strings,
 * live re-flattened values — identical to the cards detail pane; the REST show
 * endpoint is the fallback for unindexed records and non-searchable entities.
 *
 * The query key is shared, so the inspector content and the overlay header
 * subscribing for the same record dedupe into a single request.
 */
function unwrapShowResponse(payload: unknown): Record<string, unknown> {
    // Laravel API resources wrap single records in `data`.
    return (payload && typeof payload === 'object' && 'data' in (payload as object)
        ? (payload as { data: Record<string, unknown> }).data
        : payload) as Record<string, unknown>;
}

export interface ModelRecordQueryOptions {
    /**
     * Poll the record this often (ms). Entities whose state changes underneath an open
     * inspector — e.g. a LocationZonePlayer's live check-in — pass an interval so the
     * detail refreshes; static config records leave it undefined and fetch once. All
     * observers of the same record share one query key, so a single interval drives the
     * poll for every surface (content + header) without duplicate requests.
     */
    refetchIntervalMs?: number;
}

export function useModelRecordQuery(
    entityId: MaybeRefOrGetter<string>,
    recordId: MaybeRefOrGetter<number | string | null | undefined>,
    options: ModelRecordQueryOptions = {}
) {
    async function fetchRecord(): Promise<Record<string, unknown>> {
        const entity = toValue(entityId);
        const id = toValue(recordId);
        const slug = entitySlugFor(entity);

        if (isSearchableEntity(entity)) {
            const response = await http.post(`/api/${slug}/search`, {
                perPage: 1,
                withTotal: false,
                filter: {
                    ruleScopes: {
                        id: 'root',
                        scopeType: 'and',
                        rules: [{ id: 'record', column: 'id', criterion: 'equals', value: id, field: null }],
                    },
                },
            });
            const hit = (response.data as { data?: Record<string, unknown>[] }).data?.[0];
            if (hit) {
                return hit;
            }
            // Record exists but isn't indexed (yet) — fall through to the show endpoint.
        }

        return http.get(`/api/${slug}/${id}`).then((response) => unwrapShowResponse(response.data));
    }

    return useQuery({
        queryKey: computed(() => ['model-record', toValue(entityId), String(toValue(recordId))]),
        queryFn: fetchRecord,
        enabled: computed(() => Boolean(entitySlugFor(toValue(entityId)) && toValue(recordId))),
        meta: computed(() => liveRecords(toValue(entityId), [toValue(recordId)])),
        refetchInterval: options.refetchIntervalMs ?? false,
        // Polling records are live views (e.g. a player's check-in state). vue-query
        // skips interval ticks while the tab is hidden, so also keep ticking in the
        // background and refetch on refocus (overriding the app-level
        // refetchOnWindowFocus: false) — otherwise an inspector left open in a
        // background tab shows frozen state when the user returns. One-shot
        // records (no interval) keep the default fetch-once behavior.
        refetchIntervalInBackground: options.refetchIntervalMs !== undefined,
        refetchOnWindowFocus: options.refetchIntervalMs !== undefined,
        retry: 0,
    });
}
